import React, { useCallback, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { append, equals, includes, not, reject } from "ramda";
import { notBlank, notEmpty } from "../../util/ext";

import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "white",
    background: "white",
    padding: theme.spacing(4),
  },

  leftContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "33%",
  },

  rightContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },

  title: {
    flexGrow: 1,
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      alignItems: "center",
    },
  },

  count: {
    marginRight: 10,
  },

  addButton: {
    margin: 20,
  },

  search: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "4px",
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
  },

  searchIcon: {
    color: theme.palette.primary.darker,
  },

  inputRoot: {
    color: theme.palette.primary.darker,
    flexGrow: 1,
  },

  inputInput: {
    width: "100%",
    padding: theme.spacing(2.5),
  },
}));

const SearchBar = ({
  backButton,
  query,
  onQueryChange,
  otherFilters,
  filterMenu,
}) => {
  const classes = useStyles();

  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchTermChange = useCallback(
    event => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm],
  );

  const handleSearchTermEnterKey = useCallback(
    event => {
      if (event.key === "Enter") {
        if (notEmpty(searchTerm) && not(includes(searchTerm, query))) {
          onQueryChange(append(searchTerm, query));
          setSearchTerm("");
        }
      }
    },
    [searchTerm, query, onQueryChange, setSearchTerm],
  );

  const handleFilterDelete = useCallback(
    value => {
      onQueryChange(reject(equals(value), query));
    },
    [query, onQueryChange],
  );

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <div className={classes.leftContainer}>
          {notBlank(backButton) && <Box mr={2}>{backButton}</Box>}
          <div className={classes.search}>
            <OutlinedInput
              autoFocus
              type="search"
              placeholder="Search…"
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
              }}
              onChange={handleSearchTermChange}
              onKeyPress={handleSearchTermEnterKey}
              inputProps={{ "aria-label": "search" }}
              value={searchTerm}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="large"
                    classes={{ root: classes.searchIcon }}
                  />
                </InputAdornment>
              }
            />
          </div>
        </div>
        {notEmpty(filterMenu) && filterMenu}
        {notEmpty(otherFilters) && otherFilters}

        <div className={classes.rightContainer}>
          {query.map(queryPart => (
            <Chip
              key={"query-part-" + queryPart}
              label={queryPart}
              onDelete={() => handleFilterDelete(queryPart)}
              variant="outlined"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
